// ShowerHardwareSetsSection.jsx
// Showers division (08 00 02) - Hardware Sets tab
// Mirrors DoorHardwareSetsSection.jsx exactly

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react';

export const ShowerHardwareSetsSection = ({
  filteredShowerHardwareSets,
  selectedShowerHardwareSetIds,
  canEditCSIItems,
  operationLoading,
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onAddNew,
  onEdit,
  onDelete,
}) => {
  return (
    <div data-testid="shower-hardware-sets-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Hardware Sets</h2>
        <div className="flex gap-2">
          {canEditCSIItems && filteredShowerHardwareSets.length > 0 && (
            <>
              <Button variant="outline" onClick={onSelectAll}>
                {selectedShowerHardwareSetIds.length === filteredShowerHardwareSets.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                onClick={onConfirmBulkDelete}
                disabled={selectedShowerHardwareSetIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedShowerHardwareSetIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Button onClick={onAddNew} data-testid="add-shower-hardware-set-btn" disabled={operationLoading}>
              {operationLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Hardware Set
            </Button>
          )}
        </div>
      </div>

      {filteredShowerHardwareSets.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">No hardware sets created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredShowerHardwareSets.map((hardwareSet) => (
            <Card key={hardwareSet.id} data-testid={`shower-hardware-set-${hardwareSet.id}`} className={`${selectedShowerHardwareSetIds.includes(hardwareSet.id) ? 'ring-2 ring-blue-500' : ''}`}>
              <div className="flex items-center p-4">
                {canEditCSIItems && (
                  <div className="mr-4">
                    <Checkbox
                      checked={selectedShowerHardwareSetIds.includes(hardwareSet.id)}
                      onCheckedChange={() => onToggleSelection(hardwareSet.id, null)}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{hardwareSet.name}</h3>
                </div>
                <div className="mr-6">
                  <span className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                    {hardwareSet.items?.length || 0} item{(hardwareSet.items?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                {canEditCSIItems && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => onEdit(hardwareSet)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onDelete(hardwareSet.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShowerHardwareSetsSection;
